import { LineItem } from "../../../interfaces/LineItem";

interface AlternateFilterOptions {
  excludeAccepted?: boolean;
  excludeRejected?: boolean;
  filterByType?: string;
  filterByAlternate?: string[];
}

const isAlternateAccepted = (alt: LineItem): boolean => {
  const status = String(alt.status ?? "").toLowerCase();
  return alt.accepted === true || status === "accepted";
};

const isAlternateRejected = (alt: LineItem): boolean => {
  const status = String(alt.status ?? "").toLowerCase();
  return status === "rejected";
};

export const hasAlternateFilters = (options: AlternateFilterOptions): boolean => {
  const selectedAlternates = (options.filterByAlternate ?? [])
    .map((id) => String(id).trim())
    .filter((id) => id.length > 0);
  return (
    options.excludeAccepted === true ||
    options.excludeRejected === true ||
    String(options.filterByType ?? "").trim().length > 0 ||
    selectedAlternates.length > 0
  );
};

export const alternatePassesFilters = (alt: LineItem, options: AlternateFilterOptions): boolean => {
  const excludeAccepted = options.excludeAccepted === true;
  const excludeRejected = options.excludeRejected === true;
  const filterByAlternateIds = new Set(
    (options.filterByAlternate ?? []).map((id) => String(id).trim()).filter((id) => id.length > 0)
  );
  // Normalize both sides: option values may be stored raw ("target_value_option") or
  // display-formatted ("Target Value Option") depending on when the workbook persisted them,
  // and alternates arrive raw or formatted depending on caller. Deliberately NOT
  // formatDisplayValue — its date detection parses strings like "Option 1" as dates.
  const normalizeAlternateType = (value: unknown): string =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/_/g, " ");
  const filterByType = normalizeAlternateType(options.filterByType);

  if (excludeAccepted && isAlternateAccepted(alt)) return false;
  if (excludeRejected && isAlternateRejected(alt)) return false;
  if (filterByAlternateIds.size > 0 && !filterByAlternateIds.has(String(alt.id ?? "").trim())) return false;
  if (filterByType.length > 0 && normalizeAlternateType(alt.type) !== filterByType) {
    return false;
  }

  return true;
};

export const filterLineItemsByAlternates = (lineItems: LineItem[], alternates: LineItem[]): LineItem[] => {
  const allowedAlternateIds = new Set(alternates.map((alt) => String(alt.id)));
  const allowedAlternateKeys = new Set(
    alternates.map((alt) => String(alt.key ?? "").trim()).filter((key) => key.length > 0)
  );
  const allowedAlternateNames = new Set(
    alternates.map((alt) => `${alt.key} - ${alt.label}`.trim()).filter((name) => name.length > 0)
  );

  return lineItems.filter((item) => {
    const altId = String(item.alternateId ?? "").trim();
    const altName = String(item.alternate ?? "").trim();

    if (altId.length > 0 && allowedAlternateIds.has(altId)) return true;
    if (altName.length > 0 && (allowedAlternateNames.has(altName) || allowedAlternateKeys.has(altName))) return true;
    return false;
  });
};
